import { FlyerTemplateCreatorEffects } from "../pages/flyer-template-creator/store/flyer-template-creator.effects";
import { iif, of } from "rxjs";
import { catchError, filter, map, mergeMap } from "rxjs/operators";

import { addMessage } from "../../../redux/actions/messagesActions";
import { Effect } from "../../../redux/types/effect.type";
import { EffectActionsObservable } from "../../../redux/types/effect-actions-observable.type";
import { isActionType } from "../../../redux/action.functions";

import { FlyerActions } from "./flyer.actions";
import * as actions from "./flyer.actions";
import * as store from "./flyer.store";

import * as flyerService from "../services/flyer.services";
import { IFlyerFilters } from "../types/flyer-filter.interface";
import { FlyerDetailEffects } from "../pages/flyer-detail/store/flyer-detail.effects";
import { IFlyerDetailData } from "../types/flyer-detail-data/flyer-detail-data.interface";
import { StateObservable } from "redux-observable";
import { RootState } from "../../../redux/store";
import { PAGES } from "../../../features/navigation/types/pages.constant";
import { createFlyerPermissions } from "../services/flyer-permission.service";

const closeModalAndGoToFlyer = (flyer: IFlyerDetailData) =>
  of(actions.setModalOpenState(false), actions.navigateToFlyer(flyer.id));

const isOnListingPage = (state$: StateObservable<RootState>) => () =>
  state$.value.router.location.pathname === PAGES.FLYERS.URL;

const create: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerActions.Create)),
    map((action) => action.payload),
    mergeMap((form) =>
      flyerService.create(form).pipe(
        map((result) => actions.createSuccess(result)),
        catchError((error) => of(actions.createFailure(error)))
      )
    )
  );

const createSuccess: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerActions.CreateSuccess)),
    map((action) => action.payload),
    mergeMap(closeModalAndGoToFlyer)
  );

const deleteFlyer: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerActions.Delete)),
    map((action) => action.payload),
    mergeMap((flyerId) =>
      flyerService.deleteFlyer(flyerId).pipe(
        map(() => actions.deleteFlyerSuccess()),
        catchError((error) => of(actions.deleteFlyerFailure(error)))
      )
    )
  );

const deleteFlyerFailure: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerActions.DeleteFailure)),
    map((action) => action.payload),
    map((error) => addMessage({ text: error.errorMessage, severity: "error" }))
  );

const deleteFlyerSuccess: Effect = (
  action$: EffectActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(isActionType(FlyerActions.DeleteSuccess)),
    map(() => ({ filters: store.getFilters(state$.value) })),
    mergeMap(({ filters }) =>
      iif(
        isOnListingPage(state$),
        of(actions.fetch(filters)),
        of(actions.navigateToListing(filters))
      )
    )
  );

const fetchFilterOptions: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerActions.FetchFilterOptions)),
    mergeMap(() =>
      flyerService.fetchFilterOptions().pipe(
        map((results) => actions.setFilterOptions(results)),
        catchError(() => of(actions.fetchFilterOptionsFailure()))
      )
    )
  );

const fetchFlyers: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerActions.Fetch)),
    map((action) => action.payload),
    mergeMap((filters: IFlyerFilters) =>
      flyerService.fetchFlyers(filters).pipe(
        map((results) => actions.fetchSuccess(results, filters)),
        catchError((error) => of(actions.fetchFailure(error)))
      )
    )
  );

const initPermissions: Effect = (action$: EffectActionsObservable) =>
  action$.pipe(
    filter(isActionType(FlyerActions.InitPermissions)),
    map((action) => action.payload),
    map((user) => actions.setPermissions(createFlyerPermissions(user)))
  );

export const FlyerEffects = [
  create,
  createSuccess,
  deleteFlyer,
  deleteFlyerFailure,
  deleteFlyerSuccess,
  fetchFilterOptions,
  fetchFlyers,
  initPermissions,
  ...FlyerTemplateCreatorEffects,
  ...FlyerDetailEffects,
];
